"use client";

import React, { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import {
  ChevronDown,
  ChevronUp,
  ChevronLeft,
  ChevronRight,
  CheckCircle,
  AlertCircle,
  Loader2,
  Stethoscope,
  FileText,
  Database,
  Shield,
  MapPin,
  FileCheck,
  MessageSquare,
} from "lucide-react";
import { getCompanyName } from "@/lib/textron-data-processor";
import { formatCompactIndianNumber } from "@/utils/formatting";
import { useAuth } from "@/contexts/AuthContext";
import { useEngagement } from "@/contexts/EngagementContext";

interface ABCOTDItem {
  abcotd: string;
  closing_balance: number;
  opening_balance: number;
  variance: number;
  variance_percentage: number;
}

interface ReviewNote {
  question_id: string;
  user_name: string;
  datetime: string;
  text: string;
  replies?: ReviewNote[];
}

interface SignoffItem {
  fileName: string;
  signoffEntry: {
    name: string;
    prep_so: Array<{ name: string; metadata: string }>;
    rev_so: Array<{ name: string; metadata: string }>;
    conc_so: Array<{ name: string; metadata: string }>;
    eqr_so: Array<{ name: string; metadata: string }>;
    partner_so: Array<{ name: string; metadata: string }>;
  };
}

interface DiagnosticResult {
  id: string;
  title: string;
  status: "cleared" | "issues";
  items: string[];
  totalCount: number;
  tableData?: ABCOTDItem[];
  reviewNotes?: ReviewNote[];
  signoffItems?: SignoffItem[];
  signoffType?: 'prep_so' | 'rev_so';
}

export default function Diagnostics() {
  const [running, setRunning] = useState(false);
  const [results, setResults] = useState<DiagnosticResult[]>([]);
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());
  const [pageNumbers, setPageNumbers] = useState<Record<string, number>>({});
  const [companyName, setCompanyName] = useState<string>("");
  const { user } = useAuth();
  const { currentEngagement } = useEngagement();
  const [signoffData, setSignoffData] = useState<any>(null);

  React.useEffect(() => {
    const loadCompanyName = async () => {
      try {
        const name = await getCompanyName("combined");
        setCompanyName(name || "ABC Private Limited");

      } catch (error) {
        console.error("Failed to load company name:", error);
      }
    };
    loadCompanyName();
    loadSignoffData();
  }, []);

  const toggleExpand = (id: string) => {
    const newExpanded = new Set(expandedItems);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
      // Reset page to 1 when collapsing
      setPageNumbers(prev => ({ ...prev, [id]: 1 }));
    } else {
      newExpanded.add(id);
      // Set page to 1 when expanding
      setPageNumbers(prev => ({ ...prev, [id]: 1 }));
    }
    setExpandedItems(newExpanded);
  };

  const goToPage = (id: string, page: number, totalPages: number) => {
    if (page >= 1 && page <= totalPages) {
      setPageNumbers(prev => ({ ...prev, [id]: page }));
    }
  };

  const runDiagnostics = async () => {
    setRunning(true);
    setResults([]);
    // Reset all expanded items and page numbers when starting new diagnostics
    setExpandedItems(new Set());
    setPageNumbers({});

    try {
      // Load signoff data before running diagnostics
      await loadSignoffData();

      const diagnosticResults: DiagnosticResult[] = [];

      // 1. Tailoring questions left unanswered
      const unansweredQuestions = await checkUnansweredTailoringQuestions();
      diagnosticResults.push({
        id: "tailoring-questions",
        title: "Tailoring questions left unanswered",
        status: unansweredQuestions.length === 0 ? "cleared" : "issues",
        items: unansweredQuestions,
        totalCount: unansweredQuestions.length,
      });

      // 2. ABCOTDs not concluded upon
      const unconcludedABCOTDs = await checkUnconcludedABCOTDs();
      diagnosticResults.push({
        id: "abcotds",
        title: "Scoping for ABCOTDs not concluded upon as whether scoped in or out",
        status: unconcludedABCOTDs.length === 0 ? "cleared" : "issues",
        items: [],
        totalCount: unconcludedABCOTDs.length,
        tableData: unconcludedABCOTDs,
      });

      // 3. Engagement members - Coming later
      diagnosticResults.push({
        id: "engagement-members",
        title: "Engagement members added to engagement file but not yet declared independence",
        status: "cleared",
        items: [],
        totalCount: 0,
      });

      // 4. RoMMs not assessed
      const unassessedRomms = await checkUnassessedRomms();
      diagnosticResults.push({
        id: "romms-not-assessed",
        title: "RoMMs that are not yet assessed",
        status: unassessedRomms.length === 0 ? "cleared" : "issues",
        items: unassessedRomms,
        totalCount: unassessedRomms.length,
      });

      // 5. RoMMs without controls/procedures
      const rommsWithoutControls = await checkRommsWithoutControls();
      diagnosticResults.push({
        id: "romms-without-controls",
        title: "RoMMs not marked as NRPMM not having a control and procedure linked to it",
        status: rommsWithoutControls.length === 0 ? "cleared" : "issues",
        items: rommsWithoutControls,
        totalCount: rommsWithoutControls.length,
      });

      // 6. Review notes
      const reviewNotes = await checkReviewNotes();
      diagnosticResults.push({
        id: "review-notes",
        title: "Open review notes in the software",
        status: reviewNotes.length === 0 ? "cleared" : "issues",
        items: [],
        totalCount: reviewNotes.length,
        reviewNotes: reviewNotes,
      });

      // 7. Preparer signoff pending
      const preparerSignoffPending = await checkPreparerSignoffPending();
      diagnosticResults.push({
        id: "preparer-signoff",
        title: "Items pending to be signed off by preparer",
        status: preparerSignoffPending.length === 0 ? "cleared" : "issues",
        items: [],
        totalCount: preparerSignoffPending.length,
        signoffItems: preparerSignoffPending,
        signoffType: 'prep_so',
      });

      // 8. Reviewer signoff pending
      const reviewerSignoffPending = await checkReviewerSignoffPending();
      diagnosticResults.push({
        id: "reviewer-signoff",
        title: "Items not having reviewer sign off",
        status: reviewerSignoffPending.length === 0 ? "cleared" : "issues",
        items: [],
        totalCount: reviewerSignoffPending.length,
        signoffItems: reviewerSignoffPending,
        signoffType: 'rev_so',
      });

      // 10. Coming later
      diagnosticResults.push({
        id: "key-roles",
        title: "Key roles not yet assigned (archive submitter, archive approver, EQR)",
        status: "cleared",
        items: [],
        totalCount: 0,
      });

      // 11. TB Mapping incomplete
      const incompleteMappings = await checkIncompleteTBMapping();
      diagnosticResults.push({
        id: "tb-mapping",
        title: "TB Mapping is not complete",
        status: incompleteMappings.length === 0 ? "cleared" : "issues",
        items: incompleteMappings,
        totalCount: incompleteMappings.length,
      });

      setResults(diagnosticResults);
    } catch (error) {
      console.error("Error running diagnostics:", error);
    } finally {
      setRunning(false);
    }
  };

  const checkUnansweredTailoringQuestions = async (): Promise<string[]> => {
    const unanswered: string[] = [];

    try {
      // Load all tailoring questions from different areas
      const areas = [
        { name: "Planning", file: "Planning_UserResponses.json" },
        { name: "Execution", file: "Execution_TailoringQuestions_Responses.json" },
        { name: "ConcludingProcedures", file: "ConcludingProcedures_TailoringQuestions_Responses.json" },
      ];

      for (const area of areas) {
        try {
          const result = await (window as any).sharePointAPI?.downloadClientJson?.(area.file);
          if (result?.success && result?.data) {
            const data = result.data;

            // Check each area's responses
            if (area.name === "Planning") {
              // Planning_UserResponses.json structure: { [companyName]: { [questionId]: { answer: string } } }
              const planningData = data[companyName] || {};
              Object.keys(planningData).forEach((questionId) => {
                const response = planningData[questionId];
                const hasAnswer = response?.answer && response.answer.trim() !== "";
                const hasTextResponse = response?.textResponse && (
                  (response.textResponse.html && response.textResponse.html.trim() !== "") ||
                  (response.textResponse.plainText && response.textResponse.plainText.trim() !== "")
                );
                const hasDocuments =
                  response?.documents &&
                  Array.isArray(response.documents) &&
                  response.documents.length > 0;
                if (!hasAnswer && !hasTextResponse && !hasDocuments) {
                  unanswered.push(`${area.name}: ${questionId}`);
                }
              });
            } else {
              // Execution and ConcludingProcedures structure: { [areaName]: { [questionId]: { selectedOption, textResponse, ... } } }
              Object.keys(data).forEach((areaName) => {
                const areaData = data[areaName];
                if (areaData && typeof areaData === "object") {
                  Object.keys(areaData).forEach((questionId) => {
                    const questionData = areaData[questionId];
                    const hasSelectedOption = questionData?.selectedOption && questionData.selectedOption.trim() !== "";
                    const hasTextResponse = questionData?.textResponse && (
                      (questionData.textResponse.html && questionData.textResponse.html.trim() !== "") ||
                      (questionData.textResponse.plainText && questionData.textResponse.plainText.trim() !== "")
                    );
                    const hasTable = questionData?.table && Array.isArray(questionData.table) && questionData.table.length > 0;
                    const hasDocuments =
                      questionData?.documents &&
                      Array.isArray(questionData.documents) &&
                      questionData.documents.length > 0;
                    if (!hasSelectedOption && !hasTextResponse && !hasTable && !hasDocuments) {
                      unanswered.push(`${areaName}: ${questionId}`);
                    }
                  });
                }
              });
            }
          }
        } catch (error) {
          console.warn(`Failed to check ${area.name} tailoring questions:`, error);
        }
      }
    } catch (error) {
      console.error("Error checking unanswered tailoring questions:", error);
    }

    return unanswered;
  };

  const checkUnconcludedABCOTDs = async (): Promise<ABCOTDItem[]> => {
    const unconcluded: ABCOTDItem[] = [];

    try {
      // Load Planning_UserResponses.json from SharePoint
      const result = await (window as any).sharePointAPI?.downloadClientJson?.("Planning_UserResponses.json");
      if (result?.success && result?.data) {
        const data = result.data;
        const companyData = data[companyName] || {};

        // Check PreliminaryAnalyticalProcedures_ProfitLossItems
        const profitLossItems = companyData["PreliminaryAnalyticalProcedures_ProfitLossItems"];
        if (Array.isArray(profitLossItems)) {
          profitLossItems.forEach((item: any) => {
            // Check if qualitatively_material or considered_significant fields are blank
            const qualitativelyMaterial = item.qualitatively_material;
            const consideredSignificant = item.considered_significant;
            const isQualitativelyMaterialBlank = !qualitativelyMaterial ||
              (typeof qualitativelyMaterial === 'string' && qualitativelyMaterial.trim() === "");
            const isConsideredSignificantBlank = !consideredSignificant ||
              (typeof consideredSignificant === 'string' && consideredSignificant.trim() === "");

            if (isQualitativelyMaterialBlank || isConsideredSignificantBlank) {
              unconcluded.push({
                abcotd: item.abcotd || item.par || item.note_line || "Unknown ABCOTD",
                closing_balance: item.closing_balance || 0,
                opening_balance: item.opening_balance || 0,
                variance: item.variance || 0,
                variance_percentage: item.variance_percentage || 0,
              });
            }
          });
        }

        // Check PreliminaryAnalyticalProcedures_BalanceSheetItems
        const balanceSheetItems = companyData["PreliminaryAnalyticalProcedures_BalanceSheetItems"];
        if (Array.isArray(balanceSheetItems)) {
          balanceSheetItems.forEach((item: any) => {
            // Check if qualitatively_material or considered_significant fields are blank
            const qualitativelyMaterial = item.qualitatively_material;
            const consideredSignificant = item.considered_significant;
            const isQualitativelyMaterialBlank = !qualitativelyMaterial ||
              (typeof qualitativelyMaterial === 'string' && qualitativelyMaterial.trim() === "");
            const isConsideredSignificantBlank = !consideredSignificant ||
              (typeof consideredSignificant === 'string' && consideredSignificant.trim() === "");

            if (isQualitativelyMaterialBlank || isConsideredSignificantBlank) {
              unconcluded.push({
                abcotd: item.abcotd || item.par || item.note_line || "Unknown ABCOTD",
                closing_balance: item.closing_balance || 0,
                opening_balance: item.opening_balance || 0,
                variance: item.variance || 0,
                variance_percentage: item.variance_percentage || 0,
              });
            }
          });
        }
      }
    } catch (error) {
      console.error("Error checking unconcluded ABCOTDs:", error);
    }

    return unconcluded;
  };

  const checkReviewNotes = async (): Promise<ReviewNote[]> => {
    const notes: ReviewNote[] = [];

    try {
      // Load Review_Comments.json from SharePoint
      const result = await (window as any).sharePointAPI?.downloadClientJson?.("Review_Comments.json");
      if (result?.success && result?.data) {
        const data = result.data;
        if (data.comments && Array.isArray(data.comments)) {
          // Flatten all comments including replies
          const flattenComments = (comments: any[]): ReviewNote[] => {
            const flattened: ReviewNote[] = [];
            comments.forEach((comment: any) => {
              flattened.push({
                question_id: comment.question_id || "",
                user_name: comment.user_name || "",
                datetime: comment.datetime || "",
                text: comment.text || "",
                replies: comment.replies || [],
              });
              if (comment.replies && Array.isArray(comment.replies)) {
                flattened.push(...flattenComments(comment.replies));
              }
            });
            return flattened;
          };
          notes.push(...flattenComments(data.comments));
        }
      }
    } catch (error) {
      console.error("Error checking review notes:", error);
    }

    return notes;
  };

  const loadSignoffData = async () => {
    try {
      const res = await (window as any)?.sharePointAPI?.downloadSignoffJson?.();
      if (res?.success) {
        setSignoffData(res.data || { signoff: [] });
      } else {
        setSignoffData({ signoff: [] });
      }
    } catch (e: any) {
      console.error("Error loading signoff data:", e);
      setSignoffData({ signoff: [] });
    }
  };

  const checkPreparerSignoffPending = async (): Promise<SignoffItem[]> => {
    const pending: SignoffItem[] = [];

    try {
      const res = await (window as any)?.sharePointAPI?.downloadSignoffJson?.();
      if (res?.success && res.data?.signoff) {
        const signoffEntries = res.data.signoff;
        signoffEntries.forEach((entry: any) => {
          // Check if prep_so has any entry with blank or empty name
          const prepSo = entry.prep_so || [];
          const hasBlankPrepSo = prepSo.length === 0 ||
            prepSo.every((so: any) => !so.name || so.name.trim() === "");

          if (hasBlankPrepSo) {
            pending.push({
              fileName: entry.name || "",
              signoffEntry: entry,
            });
          }
        });
      }
    } catch (error) {
      console.error("Error checking preparer signoff pending:", error);
    }

    return pending;
  };

  const checkReviewerSignoffPending = async (): Promise<SignoffItem[]> => {
    const pending: SignoffItem[] = [];

    try {
      const res = await (window as any)?.sharePointAPI?.downloadSignoffJson?.();
      if (res?.success && res.data?.signoff) {
        const signoffEntries = res.data.signoff;
        signoffEntries.forEach((entry: any) => {
          // Check if rev_so has any entry with blank or empty name
          const revSo = entry.rev_so || [];
          const hasBlankRevSo = revSo.length === 0 ||
            revSo.every((so: any) => !so.name || so.name.trim() === "");

          if (hasBlankRevSo) {
            pending.push({
              fileName: entry.name || "",
              signoffEntry: entry,
            });
          }
        });
      }
    } catch (error) {
      console.error("Error checking reviewer signoff pending:", error);
    }

    return pending;
  };

  const checkUnassessedRomms = async (): Promise<string[]> => {
    const unassessed: string[] = [];

    try {
      if ((window as any).sharePointAPI?.readRommLibrary) {
        const result = await (window as any).sharePointAPI.readRommLibrary();
        if (result?.success && result?.data?.romm_library) {
          const romms = Array.isArray(result.data.romm_library)
            ? result.data.romm_library
            : [];

          romms.forEach((romm: any) => {
            const assessment = romm.assesment || romm.assessment || "";
            if (!assessment || assessment.trim() === "") {
              unassessed.push(`ROMM-${romm["romm-id"] || "Unknown"}: ${romm.description || "No description"}`);
            }
          });
        }
      }
    } catch (error) {
      console.error("Error checking unassessed RoMMs:", error);
    }

    return unassessed;
  };

  const checkRommsWithoutControls = async (): Promise<string[]> => {
    const withoutControls: string[] = [];

    try {
      if ((window as any).sharePointAPI?.readRommLibrary) {
        const result = await (window as any).sharePointAPI.readRommLibrary();
        if (result?.success && result?.data?.romm_library) {
          const romms = Array.isArray(result.data.romm_library)
            ? result.data.romm_library
            : [];

          romms.forEach((romm: any) => {
            const assessment = (romm.assesment || romm.assessment || "").toLowerCase();
            // Check if not NRPMM and not blank
            if (assessment && !assessment.includes("nrpmm") && assessment.trim() !== "") {
              const controlIds = romm.controlIds || romm.control_id || [];
              const procedureIds = romm.procedureIds || romm.procedure_id || [];

              const hasControls = Array.isArray(controlIds) && controlIds.length > 0;
              const hasProcedures = Array.isArray(procedureIds) && procedureIds.length > 0;

              if (!hasControls && !hasProcedures) {
                withoutControls.push(`ROMM-${romm["romm-id"] || "Unknown"}: ${romm.description || "No description"}`);
              }
            }
          });
        }
      }
    } catch (error) {
      console.error("Error checking RoMMs without controls:", error);
    }

    return withoutControls;
  };

  const checkIncompleteTBMapping = async (): Promise<string[]> => {
    const incomplete: string[] = [];

    try {
      if ((window as any).sharePointAPI?.downloadTrialBalanceMapping) {
        const result = await (window as any).sharePointAPI.downloadTrialBalanceMapping({ includeAmounts: true });
        if (result?.success && result?.data) {
          const mappingData = result.data.data || result.data;

          if (Array.isArray(mappingData)) {
            mappingData.forEach((entry: any) => {
              if (!entry.note_line || entry.note_line.trim() === "") {
                incomplete.push(`Ledger: ${entry.ledger_name || entry.ledger_id || "Unknown"}`);
              }
            });
          } else if (typeof mappingData === "object") {
            Object.keys(mappingData).forEach((key) => {
              const entry = mappingData[key];
              if (!entry.note_line || entry.note_line.trim() === "") {
                incomplete.push(`Ledger: ${entry.ledger_name || entry.ledger_id || key}`);
              }
            });
          }
        }
      }
    } catch (error) {
      console.error("Error checking incomplete TB mapping:", error);
    }

    return incomplete;
  };

  const getIcon = (id: string) => {
    switch (id) {
      case "tailoring-questions":
        return FileText;
      case "abcotds":
        return AlertCircle;
      case "romms-not-assessed":
      case "romms-without-controls":
        return Shield;
      case "tb-mapping":
        return MapPin;
      case "review-notes":
        return MessageSquare;
      case "preparer-signoff":
      case "reviewer-signoff":
        return FileCheck;
      default:
        return Stethoscope;
    }
  };



  const formatDate = (dateString: string): string => {
    if (!dateString) return "";
    try {
      const date = new Date(dateString);
      return date.toLocaleString();
    } catch {
      return dateString;
    }
  };

  const getInitials = (name: string): string => {
    if (!name) return "";
    const parts = name.trim().split(" ");
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  const canSignoffSection = (section: 'prep_so' | 'rev_so' | 'conc_so' | 'eqr_so' | 'partner_so'): boolean => {
    if (!user) return false;
    // Simplified logic - can be enhanced based on role requirements
    return true;
  };

  const handleSignoff = async (fileName: string, section: 'prep_so' | 'rev_so' | 'conc_so' | 'eqr_so' | 'partner_so') => {
    if (!user || !user.user_name) {
      return;
    }

    try {
      const result = await (window as any)?.sharePointAPI?.uploadSignoff?.({
        fileName: fileName,
        section: section,
        userName: user.user_name
      });

      if (result?.success) {
        await loadSignoffData();
        // Note: Diagnostics will only re-run when user clicks "Run Diagnostics" button
      }
    } catch (e: any) {
      console.error("Error updating signoff:", e);
    }
  };

  const formatItemDisplay = (result: DiagnosticResult, expanded: boolean) => {
    // Handle table data (ABCOTDs)
    if (result.tableData && result.tableData.length > 0) {
      const itemsPerPage = 10;
      const totalPages = Math.ceil(result.tableData.length / itemsPerPage);
      const currentPage = pageNumbers[result.id] || 1;
      const startIndex = (currentPage - 1) * itemsPerPage;
      const endIndex = Math.min(startIndex + itemsPerPage, result.tableData.length);
      const displayItems = result.tableData.slice(startIndex, endIndex);
      const hasMore = result.tableData.length > itemsPerPage;

      return (
        <div className="space-y-3">
          {expanded && (
            <div className="bg-black/20 rounded-lg border border-white/10 overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-white/10">
                    <th className="text-left p-3 text-sm font-semibold text-white/80">ABCOTD</th>
                    <th className="text-right p-3 text-sm font-semibold text-white/80">Closing Balance</th>
                    <th className="text-right p-3 text-sm font-semibold text-white/80">Opening Balance</th>
                    <th className="text-right p-3 text-sm font-semibold text-white/80">Variance</th>
                    <th className="text-right p-3 text-sm font-semibold text-white/80">Variance %</th>
                  </tr>
                </thead>
                <tbody>
                  {displayItems.map((item, index) => (
                    <tr key={startIndex + index} className="border-b border-white/5 hover:bg-white/5">
                      <td className="p-3 text-sm text-white/80">{item.abcotd}</td>
                      <td className="p-3 text-sm text-white/80 text-right">{formatCompactIndianNumber(item.closing_balance)}</td>
                      <td className="p-3 text-sm text-white/80 text-right">{formatCompactIndianNumber(item.opening_balance)}</td>
                      <td className="p-3 text-sm text-white/80 text-right">{formatCompactIndianNumber(item.variance)}</td>
                      <td className="p-3 text-sm text-white/80 text-right">{item.variance_percentage.toFixed(2)}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {expanded && totalPages > 1 && (
            <div className="flex items-center justify-between pt-2 border-t border-white/10">
              <div className="text-sm text-white/60">
                Showing {startIndex + 1}-{endIndex} of {result.tableData.length}
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => goToPage(result.id, currentPage - 1, totalPages)}
                  disabled={currentPage === 1}
                  className="border-white/20 text-white/80 hover:bg-white/10 hover:text-white disabled:opacity-50"
                >
                  <ChevronLeft className="h-4 w-4" />
                  Previous
                </Button>
                <div className="flex items-center gap-1">
                  {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                    let pageNum: number;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (currentPage <= 3) {
                      pageNum = i + 1;
                    } else if (currentPage >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = currentPage - 2 + i;
                    }

                    return (
                      <button
                        key={pageNum}
                        onClick={() => goToPage(result.id, pageNum, totalPages)}
                        className={`px-3 py-1 text-sm rounded-md transition-colors ${currentPage === pageNum
                            ? "bg-blue-600 text-white"
                            : "text-white/60 hover:text-white hover:bg-white/10"
                          }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => goToPage(result.id, currentPage + 1, totalPages)}
                  disabled={currentPage === totalPages}
                  className="border-white/20 text-white/80 hover:bg-white/10 hover:text-white disabled:opacity-50"
                >
                  Next
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </div>
      );
    }

    // Handle review notes
    if (result.reviewNotes && result.reviewNotes.length > 0) {
      const itemsPerPage = 10;
      const totalPages = Math.ceil(result.reviewNotes.length / itemsPerPage);
      const currentPage = pageNumbers[result.id] || 1;
      const startIndex = (currentPage - 1) * itemsPerPage;
      const endIndex = Math.min(startIndex + itemsPerPage, result.reviewNotes.length);
      const displayItems = result.reviewNotes.slice(startIndex, endIndex);
      const hasMore = result.reviewNotes.length > itemsPerPage;

      return (
        <div className="space-y-3">
          {expanded && (
            <div className="bg-black/20 rounded-lg border border-white/10 p-4">
              <div className="space-y-3">
                {displayItems.map((note, index) => (
                  <div key={startIndex + index} className="border-b border-white/10 pb-3 last:border-b-0 last:pb-0">
                    <div className="flex items-start gap-3">
                      <div className="h-1.5 w-1.5 rounded-full bg-red-400 mt-2 flex-shrink-0" />
                      <div className="flex-1">
                        <div className="text-sm text-white/80 font-medium">{note.text}</div>
                        <div className="text-xs text-white/50 mt-1">
                          {note.user_name} • {note.question_id} • {formatDate(note.datetime)}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {expanded && totalPages > 1 && (
            <div className="flex items-center justify-between pt-2 border-t border-white/10">
              <div className="text-sm text-white/60">
                Showing {startIndex + 1}-{endIndex} of {result.reviewNotes.length}
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => goToPage(result.id, currentPage - 1, totalPages)}
                  disabled={currentPage === 1}
                  className="border-white/20 text-white/80 hover:bg-white/10 hover:text-white disabled:opacity-50"
                >
                  <ChevronLeft className="h-4 w-4" />
                  Previous
                </Button>
                <div className="flex items-center gap-1">
                  {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                    let pageNum: number;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (currentPage <= 3) {
                      pageNum = i + 1;
                    } else if (currentPage >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = currentPage - 2 + i;
                    }

                    return (
                      <button
                        key={pageNum}
                        onClick={() => goToPage(result.id, pageNum, totalPages)}
                        className={`px-3 py-1 text-sm rounded-md transition-colors ${currentPage === pageNum
                            ? "bg-blue-600 text-white"
                            : "text-white/60 hover:text-white hover:bg-white/10"
                          }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => goToPage(result.id, currentPage + 1, totalPages)}
                  disabled={currentPage === totalPages}
                  className="border-white/20 text-white/80 hover:bg-white/10 hover:text-white disabled:opacity-50"
                >
                  Next
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </div>
      );
    }

    // Handle signoff items
    if (result.signoffItems && result.signoffItems.length > 0 && result.signoffType) {
      const itemsPerPage = 10;
      const totalPages = Math.ceil(result.signoffItems.length / itemsPerPage);
      const currentPage = pageNumbers[result.id] || 1;
      const startIndex = (currentPage - 1) * itemsPerPage;
      const endIndex = Math.min(startIndex + itemsPerPage, result.signoffItems.length);
      const displayItems = result.signoffItems.slice(startIndex, endIndex);
      const hasMore = result.signoffItems.length > itemsPerPage;
      const section = result.signoffType;
      const canSignoff = canSignoffSection(section);

      return (
        <div className="space-y-3">
          {expanded && (
            <div className="bg-black/20 rounded-lg border border-white/10 p-4">
              <div className="space-y-2">
                {displayItems.map((item, index) => {
                  const signoffArray = item.signoffEntry[section] || [];
                  const validEntries = signoffArray.filter((entry: any) => entry.name && entry.name.trim() !== "");
                  const hasEntries = validEntries.length > 0;

                  return (
                    <div key={startIndex + index} className="flex items-center justify-between py-2 px-3 rounded-md hover:bg-white/5 transition-colors">
                      <div className="flex items-center gap-3 flex-1">
                        <div className="h-1.5 w-1.5 rounded-full bg-red-400 flex-shrink-0" />
                        <span className="text-sm text-white/80 flex-1">{item.fileName}</span>
                      </div>
                      <div className="flex items-center gap-4 flex-shrink-0">
                        <div className="w-24 flex justify-center">
                          {hasEntries && (
                            <div className="flex flex-col items-center gap-1">
                              {validEntries.slice(0, 3).map((entry: any, idx: number) => {
                                const initials = getInitials(entry.name);
                                return (
                                  <div
                                    key={idx}
                                    className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-blue-500/20 text-blue-300 text-xs font-medium border border-blue-500/30"
                                    title={`${entry.name} - ${formatDate(entry.metadata)}`}
                                  >
                                    {initials}
                                  </div>
                                );
                              })}
                              {validEntries.length > 3 && (
                                <div className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-blue-500/20 text-blue-300 text-xs font-medium border border-blue-500/30">
                                  +{validEntries.length - 3}
                                </div>
                              )}
                            </div>
                          )}
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleSignoff(item.fileName, section)}
                            disabled={!canSignoff}
                            className={`h-6 w-6 p-0 ${!canSignoff ? 'text-white/30 cursor-not-allowed' : 'text-blue-400 hover:text-blue-300 hover:bg-blue-500/10'}`}
                            title={!canSignoff ? `You don't have permission to add ${section} Signoff` : `Add ${section} Signoff`}
                          >
                            <FileCheck className="h-3 w-3" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {expanded && totalPages > 1 && (
            <div className="flex items-center justify-between pt-2 border-t border-white/10">
              <div className="text-sm text-white/60">
                Showing {startIndex + 1}-{endIndex} of {result.signoffItems.length}
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => goToPage(result.id, currentPage - 1, totalPages)}
                  disabled={currentPage === 1}
                  className="border-white/20 text-white/80 hover:bg-white/10 hover:text-white disabled:opacity-50"
                >
                  <ChevronLeft className="h-4 w-4" />
                  Previous
                </Button>
                <div className="flex items-center gap-1">
                  {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                    let pageNum: number;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (currentPage <= 3) {
                      pageNum = i + 1;
                    } else if (currentPage >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = currentPage - 2 + i;
                    }

                    return (
                      <button
                        key={pageNum}
                        onClick={() => goToPage(result.id, pageNum, totalPages)}
                        className={`px-3 py-1 text-sm rounded-md transition-colors ${currentPage === pageNum
                            ? "bg-blue-600 text-white"
                            : "text-white/60 hover:text-white hover:bg-white/10"
                          }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => goToPage(result.id, currentPage + 1, totalPages)}
                  disabled={currentPage === totalPages}
                  className="border-white/20 text-white/80 hover:bg-white/10 hover:text-white disabled:opacity-50"
                >
                  Next
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </div>
      );
    }

    // Default: handle string items
    if (result.items.length === 0) return null;

    const itemsPerPage = 10;
    const totalPages = Math.ceil(result.items.length / itemsPerPage);
    const currentPage = pageNumbers[result.id] || 1;

    // Calculate which items to show
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = Math.min(startIndex + itemsPerPage, result.items.length);
    const displayItems = result.items.slice(startIndex, endIndex);
    const hasMore = result.items.length > itemsPerPage;

    return (
      <div className="space-y-3">
        {expanded && (
          <div className="bg-black/20 rounded-lg border border-white/10 p-4">
            <div className="space-y-2">
              {displayItems.map((item, index) => (
                <div
                  key={startIndex + index}
                  className="flex items-start gap-3 text-sm text-white/80 py-2 px-3 rounded-md hover:bg-white/5 transition-colors"
                >
                  <div className="h-1.5 w-1.5 rounded-full bg-red-400 mt-2 flex-shrink-0" />
                  <span className="flex-1">{item}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Pagination Controls */}
        {expanded && totalPages > 1 && (
          <div className="flex items-center justify-between pt-2 border-t border-white/10">
            <div className="text-sm text-white/60">
              Showing {startIndex + 1}-{Math.min(endIndex, result.items.length)} of {result.items.length}
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => goToPage(result.id, currentPage - 1, totalPages)}
                disabled={currentPage === 1}
                className="border-white/20 text-white/80 hover:bg-white/10 hover:text-white disabled:opacity-50"
              >
                <ChevronLeft className="h-4 w-4" />
                Previous
              </Button>
              <div className="flex items-center gap-1">
                {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                  let pageNum: number;
                  if (totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (currentPage <= 3) {
                    pageNum = i + 1;
                  } else if (currentPage >= totalPages - 2) {
                    pageNum = totalPages - 4 + i;
                  } else {
                    pageNum = currentPage - 2 + i;
                  }

                  return (
                    <button
                      key={pageNum}
                      onClick={() => goToPage(result.id, pageNum, totalPages)}
                      className={`px-3 py-1 text-sm rounded-md transition-colors ${currentPage === pageNum
                          ? "bg-blue-600 text-white"
                          : "text-white/60 hover:text-white hover:bg-white/10"
                        }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => goToPage(result.id, currentPage + 1, totalPages)}
                disabled={currentPage === totalPages}
                className="border-white/20 text-white/80 hover:bg-white/10 hover:text-white disabled:opacity-50"
              >
                Next
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}
      </div>
    );
  };

  const clearedCount = results.filter(r => r.status === "cleared").length;
  const issuesCount = results.filter(r => r.status === "issues").length;
  const totalIssues = results.reduce((sum, r) => sum + r.totalCount, 0);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900 p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header Section */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-bold text-white mb-2">Audit Health Check</h1>
            <p className="text-white/70 text-lg">
              Comprehensive diagnostics to ensure your audit file is ready for archival
            </p>
          </div>
          <Button
            onClick={runDiagnostics}
            disabled={running}
            size="lg"
            className="bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white shadow-lg shadow-blue-500/30 px-8 py-6 text-lg font-semibold"
          >
            {running ? (
              <>
                <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                Running Diagnostics...
              </>
            ) : (
              <>
                <Stethoscope className="mr-2 h-5 w-5" />
                Run Diagnostics
              </>
            )}
          </Button>
        </div>

        {/* Summary Cards */}
        {results.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card className="bg-white/5 border-white/10 backdrop-blur-sm">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-white/60 text-sm mb-1">Total Checks</p>
                    <p className="text-3xl font-bold text-white">{results.length}</p>
                  </div>
                  <div className="h-12 w-12 rounded-full bg-blue-500/20 flex items-center justify-center">
                    <Database className="h-6 w-6 text-blue-400" />
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-green-500/10 border-green-500/30 backdrop-blur-sm">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-green-400/80 text-sm mb-1">Cleared</p>
                    <p className="text-3xl font-bold text-green-400">{clearedCount}</p>
                  </div>
                  <div className="h-12 w-12 rounded-full bg-green-500/20 flex items-center justify-center">
                    <CheckCircle className="h-6 w-6 text-green-400" />
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-red-500/10 border-red-500/30 backdrop-blur-sm">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-red-400/80 text-sm mb-1">Issues Found</p>
                    <p className="text-3xl font-bold text-red-400">{totalIssues}</p>
                  </div>
                  <div className="h-12 w-12 rounded-full bg-red-500/20 flex items-center justify-center">
                    <AlertCircle className="h-6 w-6 text-red-400" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Diagnostic Results */}
        {results.length > 0 && (
          <div className="space-y-3">
            {results.map((result) => {
              const Icon = getIcon(result.id);
              const isExpanded = expandedItems.has(result.id);
              const hasItems = result.items.length > 0 ||
                (result.tableData && result.tableData.length > 0) ||
                (result.reviewNotes && result.reviewNotes.length > 0) ||
                (result.signoffItems && result.signoffItems.length > 0);
              const currentPage = pageNumbers[result.id] || 1;
              const itemsPerPage = 10;
              const startIndex = (currentPage - 1) * itemsPerPage;
              const endIndex = Math.min(startIndex + itemsPerPage, result.totalCount);

              return (
                <Card
                  key={result.id}
                  className={`border transition-all duration-200 ${result.status === "issues"
                      ? "bg-red-500/5 border-red-500/20 hover:border-red-500/40"
                      : "bg-green-500/5 border-green-500/20 hover:border-green-500/40"
                    } backdrop-blur-sm`}
                >
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4 flex-1">
                        <div
                          className={`h-12 w-12 rounded-lg flex items-center justify-center ${result.status === "issues"
                              ? "bg-red-500/20"
                              : "bg-green-500/20"
                            }`}
                        >
                          <Icon
                            className={`h-6 w-6 ${result.status === "issues" ? "text-red-400" : "text-green-400"
                              }`}
                          />
                        </div>
                        <div className="flex-1">
                          <CardTitle className="text-white text-lg font-semibold mb-1">
                            {result.title}
                          </CardTitle>
                          {hasItems && (
                            <p className="text-sm text-white/50">
                              {result.totalCount} item{result.totalCount !== 1 ? "s" : ""} • Showing {startIndex + 1}-{endIndex} of {result.totalCount}
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        {result.status === "cleared" ? (
                          <Badge className="bg-green-500/20 text-green-400 border-green-500/40 px-3 py-1.5 text-sm font-medium">
                            <CheckCircle className="mr-1.5 h-4 w-4" />
                            Cleared
                          </Badge>
                        ) : (
                          <Badge className="bg-red-500/20 text-red-400 border-red-500/40 px-3 py-1.5 text-sm font-medium">
                            <AlertCircle className="mr-1.5 h-4 w-4" />
                            {result.totalCount} Issue{result.totalCount !== 1 ? "s" : ""}
                          </Badge>
                        )}
                        {hasItems && (
                          <button
                            onClick={() => toggleExpand(result.id)}
                            className="text-white/60 hover:text-white transition-colors p-2 hover:bg-white/10 rounded-lg"
                          >
                            {isExpanded ? (
                              <ChevronUp className="h-5 w-5" />
                            ) : (
                              <ChevronDown className="h-5 w-5" />
                            )}
                          </button>
                        )}
                      </div>
                    </div>
                  </CardHeader>
                  {hasItems && (
                    <CardContent className={`pt-0 ${isExpanded ? "pb-6" : "pb-4"}`}>
                      {formatItemDisplay(result, isExpanded)}
                    </CardContent>
                  )}
                </Card>
              );
            })}
          </div>
        )}

        {/* Empty State */}
        {results.length === 0 && !running && (
          <Card className="border-white/10 bg-white/5 backdrop-blur-sm">
            <CardContent className="py-16 text-center">
              <div className="mx-auto h-20 w-20 rounded-full bg-blue-500/20 flex items-center justify-center mb-6">
                <Stethoscope className="h-10 w-10 text-blue-400" />
              </div>
              <h3 className="text-xl font-semibold text-white mb-2">Ready to Run Diagnostics</h3>
              <p className="text-white/60 max-w-md mx-auto">
                Click "Run Diagnostics" to perform a comprehensive health check of your audit file and identify any issues that need to be addressed before archival.
              </p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}

